import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { RadialChart } from 'react-vis';
import { Card, Row, Col } from 'react-bootstrap';
import { AiFillSafetyCertificate } from 'react-icons/ai';
import { FaChartPie } from 'react-icons/fa';
import { ImInfo } from 'react-icons/im';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  commafy,
  truncateCenter,
  EngineIcon,
  engineLabels
} from 'Utils';
import { workItemLabel } from 'Utils/nomenclature';
import DiscoveryTooltip from 'DiscoveryTooltip';

function formatDuration(ms) {
  if (!ms || ms < 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export function buildOverviewRunStatLines(inProgress, runStats) {
  const source = inProgress || runStats;
  if (!source) return [];
  const {
    workItemKind, workItemsCompleted, workItemsTotal,
    total_rows, total_processed,
  } = source;
  const lines = [];
  if (workItemsTotal > 0 && workItemsCompleted != null) {
    lines.push(`${Number(workItemsCompleted).toLocaleString()} of ${Number(workItemsTotal).toLocaleString()} ${workItemLabel(workItemKind)} processed`);
  } else if (total_rows > 0 && total_processed != null) {
    lines.push(`${Number(total_processed).toLocaleString()} of ${Number(total_rows).toLocaleString()} records processed`);
  }
  const runTime = buildOverviewRunTime(inProgress, runStats);
  if (runTime) lines.push(runTime);
  // Mark partial coverage explicitly. These results are real and worth showing — a run that
  // failed at 90% after twelve hours found genuine data — but shown WITHOUT this line they
  // read as a complete sweep, and "no findings here" then means something it should not.
  const incomplete = !inProgress && INCOMPLETE_RUN_STATUSES.has(String(runStats?.status || '').toUpperCase());
  if (incomplete) {
    lines.push(partialCoverageLine(runStats));
    if (runStats?.error_message) lines.push(String(runStats.error_message));
  }
  return lines;
}

/** Terminal statuses that mean "this run did not cover everything it set out to". */
export const INCOMPLETE_RUN_STATUSES = new Set(['FAILED', 'ERROR', 'TIMEOUT', 'INTERRUPTED', 'CANCELLED', 'STOPPED']);

/**
 * Human sentence for how far an incomplete run actually got. Uses the work-item counters
 * when present so the user sees "1,999 of 2,000" rather than a bare "it failed".
 */
export function partialCoverageLine(runStats) {
  const done = Number(runStats?.workItemsCompleted);
  const total = Number(runStats?.workItemsTotal);
  const status = String(runStats?.status || '').toUpperCase();
  const verb = status === 'CANCELLED' || status === 'STOPPED' ? 'stopped' : 'did not finish';
  if (total > 0 && Number.isFinite(done)) {
    const pct = Math.floor((done / total) * 100);
    return `Run ${verb} — partial results (${pct}% of items processed)`;
  }
  return `Run ${verb} — results may be incomplete`;
}

// "Running for HH:MM:SS" while the run is live, "Ran for HH:MM:SS" once finished.
// Returns null when there isn't enough timing info to compute a duration.
export function buildOverviewRunTime(inProgress, runStats) {
  const source = inProgress || runStats;
  if (!source) return null;
  const { start_date, completed_at, creationTime } = source;
  // Running uses creationTime (start) + now; finished uses start_date + completed_at.
  const startMs = start_date ?? creationTime ?? null;
  if (inProgress && startMs != null) {
    return `Running for ${formatDuration(Date.now() - startMs)}`;
  }
  if (completed_at != null && startMs != null && completed_at > startMs) {
    return `Ran for ${formatDuration(completed_at - startMs)}`;
  }
  return null;
}

const getRiskIdentifier = (connectionDetails) => {
  if (connectionDetails.type === 'rds') {

    //find hot name
    const hostName = connectionDetails.host.split('.')[0];
    return hostName;
  } else if (connectionDetails.type === 's3') {
    return connectionDetails.bucketName;
  } else if (connectionDetails.type === 'mongodb' || connectionDetails.type === 'salesforce') {
    return connectionDetails.name
  } else if (connectionDetails.type === 'ec2') {
    return connectionDetails.name
  }
}
// File-source meta rows: slightly tighter than the mt-2/mb-2 (16px inter-row gap) the
// DB rows use — 13px between one row's value and the next row's label, so all four
// rows plus a wrapping root path stay inside the fixed-height overview row.
const fileMetaRowStyle = { marginTop: 6, marginBottom: 7 };

const OverviewDonut = (props) => {
  const [value, setValue] = useState(false);
  const history = useHistory();

  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const displayDate = (new Date(props.searchDate)).toLocaleDateString('en-US', dateOptions);
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  const displayTime = (new Date(props.searchDate)).toLocaleString('en-US', timeOptions);
  const sampleRules = props.sampleRules;
  const runStatLines = buildOverviewRunStatLines(props.inProgress, props.runStats);
  const runTime = buildOverviewRunTime(props.inProgress, props.runStats);

  return (
    <Card className="radial-chart-card shadow-sm widget-card text-left h-100">
      <div className="widget-header d-flex p-3">
        <div>
          <span>
            <FaChartPie className="mr-1 widget-header-icon widget-header-icon-lg" />
            Discovery Overview
          </span>
        </div>
      </div>
      {props.data.length > 0 ? (
        <Card.Body className='pt-0 d-flex flex-column'>
          <Row className='text-left'>
            <Col>
              <div className="entity-date-label" style={{fontSize: '12px'}}>
                Discovery ran on {displayDate} at {displayTime} using sampling rules {' '}
                <DiscoveryTooltip text='View sample rules details'>
                  <Link to={`/rules/rules-editor/${encodeURIComponent(sampleRules)}`}>
                    {sampleRules}
                  </Link>
                </DiscoveryTooltip>
                .
                {runStatLines.length > 0 && (
                  <DiscoveryTooltip
                    placement='top'
                    text={<div>{runStatLines.map((line, i) => <div key={i} className={i > 0 ? 'mt-1' : ''}>{line}</div>)}</div>}
                  >
                    <span className='ml-2'><ImInfo style={{ fontSize: '1.1em' }} color='#315DFF' /></span>
                  </DiscoveryTooltip>
                )}
              </div>
              {runTime && (
                <div className="entity-date-label" style={{ fontSize: '12px' }}>
                  {runTime}
                </div>
              )}
            </Col>
          </Row>

          <Row className='flex-grow-1'>
            <Col>
              {/* File view top-aligns both columns so the donut's top lines up with
                  the Risk Summary link; DB view keeps the centered layout. */}
              <div className={`d-flex flex-column h-100 ${props.fileSourceDetails ? 'justify-content-start' : 'justify-content-center'}`}>
                {props.connectionId != null ? (
                  <div className='mt-2 mb-2'>

                    <div >
                      <DiscoveryTooltip text='View risk details' placement='top'>
                        <Link to={`/landscape/riskprofile/${encodeURIComponent(props.connectionId)}`}>
                          Risk Summary
                        </Link>
                      </DiscoveryTooltip>
                    </div>
                  </div>
                ) : (null)}
                {props.fileSourceDetails ? (
                  // File-source view — replaces the Database/Engine rows. Data source name
                  // links to the editor by id (the saved name is allowed to be empty for
                  // sources created via the legacy generic-folder wizard, where no name
                  // was passed through to the start request).
                  <>
                    <div style={fileMetaRowStyle}>
                      <div className='mb-1 radial-card-meta-label'>Data Source Type</div>
                      <div style={{ fontSize: '14px' }}>{props.fileSourceDetails.dataSourceType}</div>
                    </div>
                    <div style={fileMetaRowStyle}>
                      <div className='mb-1 radial-card-meta-label'>Data Source Name</div>
                      <div style={{ fontSize: '14px' }}>
                        {props.fileSourceDetails.sourceId != null ? (
                          <DiscoveryTooltip text='View Data Source details' placement='top'>
                            <Link to={`/sources/source-editor/${encodeURIComponent(props.fileSourceDetails.sourceId)}`}>
                              {props.fileSourceDetails.sourceName || `#${props.fileSourceDetails.sourceId}`}
                            </Link>
                          </DiscoveryTooltip>
                        ) : (
                          props.fileSourceDetails.sourceName || '—'
                        )}
                      </div>
                    </div>
                    <div style={fileMetaRowStyle}>
                      <div className='mb-1 radial-card-meta-label'>File Type</div>
                      <div style={{ fontSize: '14px' }}>{props.fileSourceDetails.fileTypeLabel}</div>
                    </div>
                  </>
                ) : (
                  <>
                    {(props.connection || props.connectionId != null) ? (
                      <div className='mt-2 mb-2'>
                        <div className='mb-1 radial-card-meta-label'>Data Source Name</div>
                        <div style={{ fontSize: '14px' }}>
                          {props.connectionId != null ? (
                            <DiscoveryTooltip text='View Data Source details' placement='top'>
                              <Link to={`/sources/source-editor/${encodeURIComponent(props.connectionId)}`}>
                                {props.connection || `#${props.connectionId}`}
                              </Link>
                            </DiscoveryTooltip>
                          ) : (
                            props.connection
                          )}
                        </div>
                      </div>
                    ) : (null)}
                    { props.dbname ? (
                      <div className='mt-2 mb-2'>
                        <div className="mb-1 radial-card-meta-label text-capitalize">
                          { props.engine === 's3' ? 'S3 Bucket' : 'Database' }
                        </div>
                        <div style={{ fontSize:'14px'}}>
                          {truncateCenter(props.dbname, 40, '..')}
                        </div>
                      </div>
                    ) : (null)}

                    { props.engine ? (
                      <div className='mt-2 mb-2'>
                        <div className="mb-1 radial-card-meta-label">
                          Engine
                        </div>
                        <div style={{fontSize:'14px'}}>
                          {engineLabels[props.engine]}
                          {<EngineIcon classes='ml-2' size='1.5rem' engine={props.engine} />}
                        </div>
                      </div>
                    ) : (null)}
                  </>
                )}

              </div>
            </Col>
            <Col>
              <div
                className="radial-chart-container entity-donut-chart"
                // File view: cap the chart height so the circle sits at the top of the
                // row (RadialChart centers within its container, so an uncapped h-100
                // container centers the circle in the row instead). Also frees the
                // vertical space the full-width root path row below needs.
                // minHeight override: .entity-donut-chart's 225px floor would defeat
                // the shorter height here.
                style={props.fileSourceDetails ? { height: 200, minHeight: 200 } : undefined}
              >
                {value !== false ? (
                  <div className="radial-label-results-page">
                    <p className="mb-0 entity-label-text">{ value.type }</p>
                  <h5 className="mb-0">{commafy(value.theta)}</h5>
                </div>
                  ) : (
                  <div className="radial-label-results-page">
                    { props.donutLevel === 0 ? (
                        <div>
                          <h5 className="mb-0">{commafy(props.total)}</h5>
                          <p className="mb-0 entity-label-text">total</p>
                        </div>
                      ) : (
                        <div>
                          <h5 className="mb-0">{commafy(props.otherTotal)}</h5>
                          <p className="mb-0 entity-label-text">more</p>
                        </div>
                      )
                    }
                  </div>
                )}
                <AutoSizer>
                  {({height, width}) => (
                    <RadialChart
                      animation
                      getAngle={d => d.theta}
                      angleType="literal"
                      angleRange={[45, 360]}
                      data={props.data}
                      onValueMouseOver={v => setValue(v)}
                      onSeriesMouseOut={v => setValue(false)}
                      onValueClick={(v) => {
                        if (v.type !== "Low Incidence Findings") {
                          history.push({
                            pathname: `/results/${props.searchid}/${v.type}`,
                            state: { lowConfidence: props.donutLevel === 1 }
                          });
                        } else {
                          props.handleSetDonutLevel(1);
                        }
                      }}
                      width={width}
                      height={height}
                      showLabels
                      colorType="literal"
                    />)}
                </AutoSizer>
              </div>
            </Col>
          </Row>

          {props.fileSourceDetails && (
            // Root path spans the full card width — long monospace paths need more
            // room than the left meta column can give them.
            <Row>
              <Col>
                <div style={fileMetaRowStyle}>
                  <div className='mb-1 radial-card-meta-label'>Root path</div>
                  <div style={{ fontSize: '14px', fontFamily: 'monospace', wordBreak: 'break-all', marginBottom: 5 }}>
                    {props.fileSourceDetails.rootPath}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* <Row>
            { props.connection && (
              <Col>
                <div className='d-flex'>
                  <div>
                    <div className="mb-1 radial-card-meta-label">
                      Connection
                    </div>
                    <div style={{ fontSize:'14px'}}>
                      <Link to={`/sources/source-editor/${props.connection}`}>
                        { truncateCenter(props.connection, 28, '..') }
                      </Link>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            { props.engine && (
              <Col>
                <div className='d-flex'>
                  <div>
                    <div className="mb-1 radial-card-meta-label">
                      Engine
                    </div>
                    <div style={{fontSize:'14px'}}>
                      {engineLabels[props.engine]} 
                    </div>
                  </div>
                  <div className='d-flex flex-grow-1 justify-content-end h-100'>
                    <EngineIcon classes='h-100' engine={props.engine} size='40px' />
                  </div>
                </div>
              </Col>
            )}
          </Row> */}
        </Card.Body>
      ) : (
        <Card.Body className="text-center">
          <p>
            <AiFillSafetyCertificate
              className="no-data-found-icon-1"
              size="175"
            />
          </p>
          <p className="text-secondary">
            No sensitive data was found for this run.
          </p>
        </Card.Body>
      )}
    </Card>
  );
}

export default OverviewDonut;
